import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Bell, User as UserIcon, ShieldWarning, X, ArrowClockwise } from "@phosphor-icons/react";
import { Switch } from "@/components/ui/switch";

/**
 * Astreinte SAV — pick which user gets a direct push whenever a new SAV
 * ticket arrives. Layered on top of the default admin fan-out, so critical
 * tickets never wait until office hours.
 */
export default function SavAstreintePanel() {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const [a, u] = await Promise.all([
        api.get("/admin/sav-astreinte"),
        api.get("/users"),
      ]);
      setState({ loading: false, data: a.data, error: null });
      setUsers((u.data || []).filter((x) => x.is_active !== false && ["admin", "associate", "technician", "compta"].includes(x.role)));
    } catch (err) {
      setState({ loading: false, data: null, error: formatApiError(err) });
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    setSaving(true);
    try {
      const r = await api.patch("/admin/sav-astreinte", { ...state.data, ...patch });
      setState((s) => ({ ...s, data: r.data }));
      toast.success("Astreinte mise à jour");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (state.loading) return <div className="text-white/50 text-sm py-10 text-center">Chargement…</div>;
  if (state.error) {
    return (
      <div className="rounded-2xl bg-red-500/10 border border-red-500/30 p-4 text-red-300 text-sm flex items-center gap-2">
        <ShieldWarning size={16} weight="duotone" /> {state.error}
      </div>
    );
  }

  const d = state.data || {};
  const cur = d.on_call_user;

  return (
    <div className="space-y-4" data-testid="astreinte-panel">
      <div className="rounded-2xl bg-gradient-to-br from-red-500/10 to-white/[0.02] border border-red-500/25 p-4">
        <div className="flex items-center gap-2 font-display font-semibold text-sm">
          <Bell size={16} weight="duotone" className="text-red-300" /> Astreinte SAV
        </div>
        <div className="text-xs text-white/60 mt-1">
          La personne d&apos;astreinte reçoit une notification push immédiate à chaque nouveau ticket SAV (webhook ou création manuelle),
          en plus des admins.
        </div>
      </div>

      <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 space-y-3">
        <Row label="Système activé">
          <Switch checked={!!d.enabled} onCheckedChange={(v) => save({ enabled: v })} disabled={saving} data-testid="astreinte-toggle-enabled" />
        </Row>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Personne d&apos;astreinte</div>
          {cur ? (
            <div className="flex items-center gap-3 bg-white/[0.03] border border-white/10 rounded-xl px-3 py-2.5" data-testid="astreinte-current">
              <div className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold shrink-0" style={{ background: cur.color || "#005CFF" }}>
                {(cur.name || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold truncate">{cur.name}</div>
                <div className="text-[10px] text-white/50 uppercase tracking-widest">{cur.role}{cur.phone ? ` · ${cur.phone}` : ""}</div>
              </div>
              <button
                onClick={() => save({ on_call_user_id: null })}
                disabled={saving}
                className="w-8 h-8 rounded-lg bg-white/5 hover:bg-red-500/20 hover:text-red-300 text-white/60 flex items-center justify-center"
                title="Retirer l&apos;astreinte"
                data-testid="astreinte-remove"
              ><X size={13} weight="bold" /></button>
            </div>
          ) : (
            <div className="text-xs text-white/50 italic bg-white/[0.02] border border-dashed border-white/10 rounded-xl px-3 py-3">
              Aucun tech d'astreinte défini — sélectionnez ci-dessous.
            </div>
          )}
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Choisir la personne d&apos;astreinte</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="astreinte-picker">
            {users.map((u) => (
              <button
                key={u.id}
                onClick={() => save({ on_call_user_id: u.id })}
                disabled={saving}
                className={`flex items-center gap-2 rounded-xl px-3 py-2 border text-left transition-colors ${d.on_call_user_id === u.id ? "bg-[#005CFF]/15 border-[#005CFF]/40" : "bg-white/[0.02] border-white/10 hover:bg-white/[0.06]"}`}
                data-testid={`astreinte-pick-${u.id}`}
              >
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0" style={{ background: u.color || "#005CFF" }}>
                  {(u.name || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-medium truncate">{u.name}</div>
                  <div className="text-[9px] text-white/45 uppercase tracking-widest">{u.role}</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        <button onClick={load} className="mt-2 text-xs text-white/60 hover:text-white flex items-center gap-1"><ArrowClockwise size={11} weight="bold" /> Recharger</button>
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-center justify-between gap-3 bg-white/[0.02] border border-white/5 rounded-lg px-3 py-2">
      <span className="text-xs text-white/80">{label}</span>
      {children}
    </div>
  );
}
